import { useState, useCallback, useEffect } from "react";
import { api } from "../../lib/api";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { RefreshCw, Lock, Clock, TrendingUp, AlertTriangle, CheckCircle2 } from "lucide-react";

const BAND_STYLE = {
    ready:      { text: "text-emerald-700", bg: "bg-emerald-50",  border: "border-emerald-200", dot: "bg-emerald-500"  },
    developing: { text: "text-amber-700",   bg: "bg-amber-50",    border: "border-amber-200",   dot: "bg-amber-500"    },
    not_ready:  { text: "text-red-700",     bg: "bg-red-50",      border: "border-red-200",     dot: "bg-red-500"      },
};

const DIM_LABELS = {
    financial_quality:      "Financial Quality",
    documentation_readiness: "Documentation Readiness",
    buyer_attractiveness:   "Buyer Attractiveness",
    vendor_motivation:      "Vendor Motivation",
    market_attractiveness:  "Market Attractiveness",
};

function hoursAgo(isoStr) {
    if (!isoStr) return null;
    const ms = Date.now() - new Date(isoStr).getTime();
    const h = Math.floor(ms / 3600000);
    if (h < 1) return "less than an hour ago";
    if (h === 1) return "1 hour ago";
    return `${h} hours ago`;
}

export function ReadinessTab({ dealId, stage }) {
    const isTerminal = stage === "Completed" || stage === "Lost";

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const load = useCallback(async (forceRefresh = false) => {
        if (isTerminal) return;
        setLoading(true);
        try {
            const url = forceRefresh
                ? `/deals/${dealId}/readiness?refresh=true`
                : `/deals/${dealId}/readiness`;
            const res = await api.get(url);
            setData(res.data);
        } catch (e) {
            if (e.response?.status === 503) {
                setData({ _error: "AI features not configured — add ANTHROPIC_API_KEY to enable this tab." });
            } else if (e.response?.status === 400) {
                setData({ _terminal: true });
            } else {
                setData({ _error: e.response?.data?.detail || "Could not load readiness assessment." });
            }
        } finally {
            setLoading(false);
        }
    }, [dealId, isTerminal]);

    const handleRefresh = async () => {
        setRefreshing(true);
        await load(true);
        setRefreshing(false);
        toast.success("Readiness assessment refreshed");
    };

    useEffect(() => { load(); }, [load]);

    if (isTerminal) {
        return (
            <div className="text-sm text-muted-foreground bg-muted/40 rounded-md p-6 text-center" data-testid="readiness-terminal-msg">
                Readiness assessment is only available for active deals.
            </div>
        );
    }

    const c = data?.band ? (BAND_STYLE[data.band] || BAND_STYLE.developing) : {};

    return (
        <div className="space-y-6" data-testid="readiness-tab">
            {/* Header row */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="font-display text-xl font-bold tracking-tight">Vendor Readiness Score</h2>
                    {data?.computed_at && !data._error && (
                        <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {data.cached ? "Cached · " : ""}
                            Last assessed {hoursAgo(data.computed_at)}
                        </p>
                    )}
                </div>
                {!data?._error && (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRefresh}
                        disabled={refreshing || loading}
                        data-testid="readiness-refresh-btn"
                        className="gap-1.5"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
                        {refreshing ? "Refreshing…" : "Refresh"}
                    </Button>
                )}
            </div>

            {loading && !data ? (
                <div className="py-12 text-center text-sm text-muted-foreground" data-testid="readiness-loading">
                    <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-3 text-muted-foreground/60" />
                    Claude is assessing this business…
                </div>
            ) : data?._error ? (
                <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-4" data-testid="readiness-error">
                    <AlertTriangle className="w-4 h-4 inline mr-2" />
                    {data._error}
                </div>
            ) : data ? (
                <>
                    {/* Overall score */}
                    <div className={`border rounded-md p-6 ${c.border} ${c.bg}`} data-testid="readiness-score-card">
                        <div className="flex items-center gap-6">
                            <div>
                                <div className={`text-6xl font-bold font-mono ${c.text}`} data-testid="readiness-overall-score">
                                    {data.overall_score}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">out of 100</div>
                            </div>
                            <div>
                                <Badge
                                    className={`uppercase tracking-wider text-xs ${c.bg} ${c.text} border ${c.border}`}
                                    data-testid="readiness-band"
                                >
                                    <span className={`w-1.5 h-1.5 rounded-full ${c.dot} mr-1.5 inline-block`} />
                                    {(data.band || "").replace("_", " ")}
                                </Badge>
                                <p className="text-sm text-muted-foreground mt-2 max-w-xs">
                                    {data.overall_score >= 70
                                        ? "This business appears well prepared for a sale process."
                                        : data.overall_score >= 40
                                        ? "Some preparation is needed before going to market."
                                        : "Significant groundwork required before this business is sale-ready."}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Five dimensions */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="readiness-dimensions">
                        {(data.dimensions || []).map((dim) => {
                            const pct = dim.max_score ? dim.score / dim.max_score : 0;
                            const barColor = pct >= 0.7 ? "bg-emerald-400" : pct >= 0.4 ? "bg-amber-400" : "bg-red-400";
                            return (
                                <div key={dim.name} className="border border-border rounded-md p-4 bg-white" data-testid={`readiness-dim-${dim.name}`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="font-medium text-sm">{DIM_LABELS[dim.name] || dim.name}</span>
                                        <span className="text-xs font-mono text-muted-foreground">
                                            {dim.score}<span className="text-muted-foreground/60">/{dim.max_score}</span>
                                        </span>
                                    </div>
                                    <div className="w-full h-1.5 rounded-full bg-border/60 overflow-hidden mb-3">
                                        <div className={`h-full rounded-full ${barColor}`} style={{ width: `${pct * 100}%` }} />
                                    </div>
                                    <p className="text-xs text-muted-foreground leading-relaxed">{dim.commentary}</p>
                                </div>
                            );
                        })}
                    </div>

                    {/* Top improvements */}
                    {data.top_improvements?.length > 0 && (
                        <div className="border border-border rounded-md p-5 bg-white" data-testid="readiness-improvements">
                            <h3 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                                <TrendingUp className="w-3.5 h-3.5" /> Top Improvements
                            </h3>
                            <ol className="space-y-2">
                                {data.top_improvements.map((item, i) => (
                                    <li key={i} className="flex items-start gap-3 text-sm" data-testid={`readiness-improvement-${i}`}>
                                        <span className="w-5 h-5 rounded-full bg-foreground text-background text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                                            {i + 1}
                                        </span>
                                        {item}
                                    </li>
                                ))}
                            </ol>
                        </div>
                    )}

                    {/* Broker notes */}
                    {data.broker_notes && (
                        <div className="flex items-start gap-3 text-sm text-muted-foreground bg-muted/30 border border-border/50 rounded-md p-4"
                             data-testid="readiness-broker-notes">
                            <Lock className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                            <div>
                                <div className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground/60 mb-1">Internal note</div>
                                {data.broker_notes}
                            </div>
                        </div>
                    )}
                </>
            ) : null}
        </div>
    );
}
